/**
 * domains/attribution/attributionProcessor.ts — MER Algo Footprints v8.2.0
 *
 * Attribution Processor — the closed-loop orchestrator.
 *
 * Responsibilities:
 *   1. capture(stateResult, entryPrice, ...) → build snapshot → record to journal
 *   2. evaluate(snapshotId, bars, config?) → run evaluator → record outcome
 *   3. processPending(bars) → evaluate all pending snapshots against available bars
 *
 * This is the single insertion point for automatic attribution capture.
 * Callers pass in a StateTransitionResult; the processor handles everything else.
 *
 * No coupling back to the state engine. No engine mutation. Pure orchestration.
 */

import type { StateTransitionResult } from '../market/engines/stateTransition/types';
import type {
  AttributionSnapshot,
  AttributionOutcome,
  AttributionRecord,
  SnapshotKeyLevels,
  SnapshotRegimeContext,
  SnapshotGammaContext,
  EvaluatorConfig,
} from './types';
import type { Candle } from '../../shared/types';

import { buildAttributionSnapshot, validateSnapshot } from './attributionSnapshot';
import { evaluateOutcome } from './attributionEvaluator';
import {
  recordSnapshot,
  recordOutcome,
  getPendingSnapshots,
  getRecord,
  getJournalStats,
} from './attributionJournal';

// ─── Capture ──────────────────────────────────────────────────────────────────

export interface CaptureInput {
  symbol:         string;
  timeframe:      string;
  entryPrice:     number;
  stateResult:    StateTransitionResult;
  keyLevels?:     SnapshotKeyLevels;
  regimeContext?: SnapshotRegimeContext;
  gammaContext?:  SnapshotGammaContext;
  emittedAt?:     number;
}

export interface CaptureResult {
  snapshot:  AttributionSnapshot;
  record:    AttributionRecord;
  errors:    string[];
}

/**
 * Primary capture entry point.
 * Call this whenever the state engine emits a result you want to track.
 * Returns the persisted record. Idempotent on snapshotId.
 */
export function capture(input: CaptureInput): CaptureResult {
  const snapshot = buildAttributionSnapshot({
    symbol:         input.symbol,
    timeframe:      input.timeframe,
    emittedAt:      input.emittedAt ?? Date.now(),
    entryPrice:     input.entryPrice,
    stateResult:    input.stateResult,
    keyLevels:      input.keyLevels,
    regimeContext:  input.regimeContext,
    gammaContext:   input.gammaContext,
  });

  const errors = validateSnapshot(snapshot);
  if (errors.length > 0) {
    // Return the snapshot with errors — caller decides whether to proceed
    return { snapshot, record: { snapshot, status: 'pending', recordedAt: Date.now() }, errors };
  }

  const record = recordSnapshot(snapshot);
  return { snapshot, record, errors: [] };
}

// ─── Evaluate ─────────────────────────────────────────────────────────────────

export interface EvaluateResult {
  outcome:  AttributionOutcome;
  record:   AttributionRecord | undefined;
  skipped:  boolean;
  reason?:  string;
}

/**
 * Evaluate a specific snapshot against bars.
 * Skips if already evaluated.
 * Records outcome to journal.
 */
export function evaluate(
  snapshotId: string,
  bars:       Candle[],
  config?:    Partial<EvaluatorConfig>,
): EvaluateResult {
  const record = getRecord(snapshotId);

  if (!record) {
    return {
      outcome:  _emptyOutcome(snapshotId),
      record:   undefined,
      skipped:  true,
      reason:   'snapshot not found in journal',
    };
  }

  if (record.status === 'evaluated') {
    return {
      outcome:  record.outcome!,
      record,
      skipped:  true,
      reason:   'already evaluated',
    };
  }

  const outcome = evaluateOutcome(record.snapshot, bars, config);
  recordOutcome(outcome);

  return {
    outcome,
    record:  getRecord(snapshotId),
    skipped: false,
  };
}

// ─── Process pending ──────────────────────────────────────────────────────────

export interface ProcessPendingResult {
  processed:  number;
  skipped:    number;
  errors:     string[];
}

/**
 * Evaluate all pending snapshots against provided bars.
 *
 * In practice, callers pass the most recent N candles for the relevant symbol.
 * The processor evaluates each pending snapshot using bars that arrived after
 * the snapshot's emittedAt timestamp.
 *
 * @param symbolBars  Map of symbol → Candle[] (chronological order)
 * @param minBars     Minimum bar count required to evaluate (default: 5)
 */
export function processPending(
  symbolBars: Map<string, Candle[]>,
  minBars = 5,
): ProcessPendingResult {
  const pending = getPendingSnapshots();
  let processed = 0;
  let skipped   = 0;
  const errors: string[] = [];

  for (const record of pending) {
    const { snapshot } = record;
    const bars = symbolBars.get(snapshot.symbol);

    if (!bars || bars.length === 0) {
      skipped++;
      continue;
    }

    // Only use bars that arrived after the snapshot was emitted
    const forwardBars = bars.filter(b => b.ts > snapshot.emittedAt);

    if (forwardBars.length < minBars) {
      skipped++;
      continue;
    }

    try {
      const outcome = evaluateOutcome(snapshot, forwardBars);
      recordOutcome(outcome);
      processed++;
    } catch (err: any) {
      errors.push(`${snapshot.snapshotId}: ${err.message}`);
      skipped++;
    }
  }

  return { processed, skipped, errors };
}

// ─── Status summary ───────────────────────────────────────────────────────────

export interface ProcessorStatus {
  journalStats: ReturnType<typeof getJournalStats>;
  pendingCount: number;
}

export function getProcessorStatus(): ProcessorStatus {
  const stats = getJournalStats();
  return {
    journalStats: stats,
    pendingCount: stats.pending,
  };
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function _emptyOutcome(snapshotId: string): AttributionOutcome {
  return {
    snapshotId,
    symbol:        'UNKNOWN',
    state:         'NO_VALID_SETUP',
    evaluatedAt:   Date.now(),
    barsConsumed:  0,
    longTriggered: false,
    shortTriggered:false,
    invalidated:   false,
    firstEvent:    'none',
    firstEventBar: null,
    mfe:           0,
    mae:           0,
    horizonResults:{},
    notes:         ['snapshot not found'],
  };
}
